import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { User, UserRole } from '../../models/User';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userId: string, userData: {
    name: string;
    role: UserRole;
    supervisorId?: string;
    badgeNumber?: string;
    department?: string;
    credentials?: string;
    specializations?: string[];
  }) => Promise<void>;
  user: User | null;
  supervisors: User[];
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  supervisors,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    role: 'rbt' as UserRole,
    supervisorId: '',
    badgeNumber: '',
    department: '',
    credentials: '',
    specializations: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        name: user.name || '',
        role: user.role || 'rbt',
        supervisorId: user.supervisorId || '',
        badgeNumber: user.badgeNumber || '',
        department: user.department || '',
        credentials: user.credentials || '',
        specializations: user.specializations?.join(', ') || '',
      });
      setError(null);
    }
  }, [isOpen, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setError(null);
    setLoading(true);

    try {
      const specializationsArray = formData.specializations
        ? formData.specializations.split(',').map((s) => s.trim()).filter((s) => s)
        : undefined;

      await onSubmit(user.id, {
        name: formData.name,
        role: formData.role,
        supervisorId: (formData.role === 'rbt' || formData.role === 'supervisor') && formData.supervisorId ? formData.supervisorId : undefined,
        badgeNumber: formData.badgeNumber || undefined,
        department: formData.department || undefined,
        credentials: formData.credentials || undefined,
        specializations: specializationsArray,
      });

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          margin: 'var(--spacing-4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          <h2 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>
            Edit User
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-gray-400)',
              padding: 'var(--spacing-2)',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-4)' }}>
            {error}
          </div>
        )}

        <div
          style={{
            marginBottom: 'var(--spacing-4)',
            padding: 'var(--spacing-3)',
            backgroundColor: 'var(--color-gray-50)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
            <strong>Email:</strong> {user.email}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="name">
              Full Name *
            </label>
            <input
              id="name"
              type="text"
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Doe"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="role">
              Role *
            </label>
            <select
              id="role"
              className="form-input"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              required
            >
              <option value="rbt">RBT</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Admin</option>
              <option value="hr">HR Manager</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>

          {(formData.role === 'rbt' || formData.role === 'supervisor') && (
            <div className="form-group">
              <label className="form-label" htmlFor="supervisor">
                {formData.role === 'rbt' ? 'Assign Supervisor' : 'Assign Supervising Supervisor'}
              </label>
              <select
                id="supervisor"
                className="form-input"
                value={formData.supervisorId}
                onChange={(e) => setFormData({ ...formData, supervisorId: e.target.value })}
              >
                <option value="">Unassigned</option>
                {supervisors.map((supervisor) => (
                  <option key={supervisor.id} value={supervisor.id}>
                    {supervisor.name} ({supervisor.email})
                  </option>
                ))}
              </select>
              {formData.role === 'supervisor' && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginTop: 'var(--spacing-2)' }}>
                  Supervisors can also be assigned to other supervisors for oversight
                </p>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="badgeNumber">
              Badge Number
            </label>
            <input
              id="badgeNumber"
              type="text"
              className="form-input"
              value={formData.badgeNumber}
              onChange={(e) => setFormData({ ...formData, badgeNumber: e.target.value })}
              placeholder="e.g., RBT-001, SUP-002"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="department">
              Department
            </label>
            <input
              id="department"
              type="text"
              className="form-input"
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              placeholder="e.g., ABA Services, Early Intervention"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="credentials">
              Credentials
            </label>
            <input
              id="credentials"
              type="text"
              className="form-input"
              value={formData.credentials}
              onChange={(e) => setFormData({ ...formData, credentials: e.target.value })}
              placeholder="e.g., BCBA, RBT, OT, SLP"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="specializations">
              Specializations
            </label>
            <input
              id="specializations"
              type="text"
              className="form-input"
              value={formData.specializations}
              onChange={(e) => setFormData({ ...formData, specializations: e.target.value })}
              placeholder="e.g., ABA, Early Intervention, Autism"
            />
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginTop: 'var(--spacing-2)' }}>
              Comma-separated areas of expertise
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-3)',
              marginTop: 'var(--spacing-6)',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              style={{ flex: 1 }}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ flex: 1 }}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
